import React, { useEffect, useCallback } from "react";

import { Paper, Box } from "@material-ui/core";
import { useObservableState, useObservable, useObservableCallback, identity } from "observable-hooks";
import { combineLatest, of, forkJoin } from "rxjs";
import { map, switchMap, catchError } from "rxjs/operators";
import type { SiteStatistics, IIssue, ISiteSlim, IResponse, IResponseWithLoading } from "App/Modules/Models";
import { extendWithLoadingAndFail } from "App/Modules/Services";
import type {
    ISitesRepository,
    IIssuesRepository,
    ISiteScheduleExceptionsRepository,
    ISiteVideoStreamsRepository,
} from "App/Modules/Repositories";
import type { RefreshIntervalSeconds } from "App/ReactComponents/Shared";
import {
    applyLocalUpdatesOnFullDataArray,
    getUserProfile,
    hasUserAccessToMonitoring,
    hasUserAccessToSiteInformation,
    loadingCompletedResponse,
    mergeLoadingAndDataWithPrevious,
    mergeResponsesWithLoading,
} from "App/ReactComponents/Shared";
import { combinedSitesSelector } from "../../Selectors";

import { SitesListUI } from "./SitesListUI";

type InjectedProps = {
    mmSitesRepository: ISitesRepository;
    mmIssuesRepository: IIssuesRepository;
    mmSiteScheduleExceptionsRepository: ISiteScheduleExceptionsRepository;
    mmSiteVideoStreamsRepository: ISiteVideoStreamsRepository;
};

type Props = InjectedProps;

export const SitesList = (props: Props) => {
    const [onRefresh, refresh$] = useObservableCallback<RefreshIntervalSeconds>(identity);
    const [onLocalIssueUpdate, localIssueUpdate$] = useObservableCallback<IResponseWithLoading<IIssue[]>>(identity);

    const profile = getUserProfile();
    const showStatistics = hasUserAccessToSiteInformation(profile);
    const showMonitoring = hasUserAccessToMonitoring(profile);

    const $sites = useObservable(() =>
        refresh$.pipe(
            switchMap(() =>
                extendWithLoadingAndFail(
                    props.mmSitesRepository.getSites().pipe(catchError((e: IResponse<ISiteSlim[]>) => of(e))),
                ),
            ),
            mergeLoadingAndDataWithPrevious(),
        ),
    );

    const $sitesStatistics = useObservable(() =>
        !showStatistics
            ? of(loadingCompletedResponse<SiteStatistics[]>())
            : refresh$.pipe(
                  switchMap(() =>
                      extendWithLoadingAndFail(
                          props.mmSitesRepository
                              .getSitesStatistics()
                              .pipe(catchError((e: IResponse<SiteStatistics[]>) => of(e))),
                      ),
                  ),
                  mergeLoadingAndDataWithPrevious(),
              ),
    );

    const $issues = useObservable(() => {
        if (!showMonitoring) {
            return of(loadingCompletedResponse<IIssue[]>());
        }

        const dbIssues$ = refresh$.pipe(
            switchMap(() =>
                extendWithLoadingAndFail(
                    props.mmIssuesRepository.getActiveIssues().pipe(catchError((e: IResponse<IIssue[]>) => of(e))),
                ),
            ),
        );

        return applyLocalUpdatesOnFullDataArray(dbIssues$, localIssueUpdate$);
    });

    const $sitesCombined = useObservable(() =>
        combineLatest([$sites, $sitesStatistics, $issues]).pipe(
            map(([sites, sitesStatistics, issues]) => {
                return combinedSitesSelector({
                    sites,
                    sitesStatistics,
                    issues,
                });
            }),
        ),
    );

    const updateIssues = useCallback((...issuesToUpdate: Array<Partial<IIssue>>) => {
        if (!issuesToUpdate.length) {
            return;
        }

        extendWithLoadingAndFail(
            forkJoin(
                issuesToUpdate
                    .filter((issue) => issue.isAcknowledged)
                    .map((issue) =>
                        props.mmIssuesRepository
                            .acknowledgeIssue(issue.id)
                            .pipe(catchError((e: IResponse<ISiteSlim[]>) => of(e))),
                    ),
            ).pipe(
                map((responses) => {
                    const loadingStates = responses.map(
                        ({ data, ...loadingState }) => loadingState as IResponseWithLoading<void>,
                    );
                    const loadingState = mergeResponsesWithLoading(...loadingStates);
                    const data = responses.map((r) => r.data);
                    return {
                        data,
                        ...loadingState,
                    } as IResponseWithLoading<IIssue[]>;
                }),
            ),
        ).subscribe((responses) => {
            onLocalIssueUpdate(responses);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        onRefresh(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sites = useObservableState($sitesCombined);

    return (
        <Box mb={4}>
            <Paper>
                {sites && (
                    <SitesListUI
                        sites={sites}
                        siteScheduleExceptionsRepository={props.mmSiteScheduleExceptionsRepository}
                        siteVideoStreamsRepository={props.mmSiteVideoStreamsRepository}
                        onRefresh={(refreshInterval) => onRefresh(refreshInterval)}
                        updateIssues={(...updatedIssues) => updateIssues(...updatedIssues)}
                    />
                )}
            </Paper>
        </Box>
    );
};
